import { client } from 'src/enviroment/api/axios'
import { useMutation } from '@tanstack/react-query'
import { clearTokens, saveTokens } from 'src/enviroment/constants/storageKeys'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { setDefaultTenant } from 'src/enviroment/lib/utils'
import { Mutation } from '../index'
import {
  AuthResponse,
  ForgotPasswordBody,
  LoginBody,
  LoginResponse,
  RefreshTokenBody,
  ResetPasswordBody,
  SignUpBody,
  SignUpResponse,
} from 'src/types/auth'

export const authRouterKeys = {
  auth: 'auth',
} as const

export async function refreshTokenFn(body: RefreshTokenBody): Promise<AuthResponse> {
  return (await client.post<AuthResponse>('/tokens/refresh', body)).data
}

export interface LoginMutationBody extends LoginBody {
  isRemember: boolean
}

export interface SignUpMutationBody extends SignUpBody {
  isRemember: boolean
}

export const authRouter = {
  signUp: {
    useMutation: (opts?: Mutation<SignUpMutationBody, SignUpResponse>) => {
      return useMutation({
        mutationFn: async ({
          username,
          password,
          firstName,
          lastName,
          company,
          invitationToken,
          isRemember,
        }: SignUpMutationBody) => {
          const response = (
            await client.post<SignUpResponse>('/auth/local/signup', {
              username,
              password,
              firstName,
              lastName,
              company,
              invitationToken,
            })
          ).data
          saveTokens(response, isRemember, invitationToken)
          setDefaultTenant(response.tenants)
          return response
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  login: {
    useMutation: (opts?: Mutation<LoginMutationBody, LoginResponse>) => {
      return useMutation({
        mutationFn: async ({ username, password, invitationToken, isRemember }: LoginMutationBody) => {
          const response = (
            await client.post<LoginResponse>('/auth/local/login', {
              username,
              password,
              invitationToken,
            })
          ).data
          saveTokens(response, isRemember)
          setDefaultTenant(response.tenants)
          return response
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  logout: {
    useMutation: (opts?: Mutation<void, void>) => {
      return useMutation({
        mutationFn: async () => {
          clearTokens()
        },
        ...opts,
      })
    },
  },
  refreshToken: {
    useMutation: (opts?: Mutation<RefreshTokenBody, AuthResponse>) => {
      return useMutation({
        mutationFn: refreshTokenFn,
        onError: handleApiError,
        ...opts,
      })
    },
  },
  forgotPassword: {
    useMutation: (opts?: Mutation<ForgotPasswordBody, void>) => {
      return useMutation({
        mutationFn: async (body: ForgotPasswordBody) => {
          await client.post('/password/forgot', body)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  resetPassword: {
    useMutation: (opts?: Mutation<ResetPasswordBody, void>) => {
      return useMutation({
        mutationFn: async (body: ResetPasswordBody) => {
          await client.post('/password/reset', body)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
}
